import { useState } from 'react';
import { Outlet } from 'react-router-dom';

import { ArcusLoading } from '@/components/shared/ArcusLoading';
import { useAccountAccess } from '@/compositions/hooks/useAccountAccess';

import { AccountSuspendedScreen } from './AccountSuspendedScreen';

/**
 * Guarda de conta ativa (plano do painel do admin, D-05).
 *
 * Diferente da `RequireSession` — que só pergunta "há sessão?" —, esta guarda
 * pergunta "a plataforma bloqueou esta conta?". Entra **dentro** de
 * `RequireSession` nos dois lugares onde ela aparece em `src/routes/index.tsx`:
 * sem sessão a pergunta não tem sentido, e a RPC devolveria zero linhas para
 * convidado a cada tela.
 *
 * ⚠️ **Isto não é a tranca.** Quem barra toda escrita é o banco (migration
 * `account_suspension`); esta guarda só evita que a pessoa descubra o bloqueio
 * por tentativa e erro. Um `false` forjado aqui não abriria nada — a RLS
 * continua recusando.
 *
 * Consequência assumida: as rotas **públicas** (mapa, conteúdo, doações,
 * instituições, perfil) ficam **fora** desta guarda de propósito — é um app de
 * resposta a desastre, e o bloqueio tira a participação, não o socorro.
 *
 * O estado de carregamento é obrigatório pelo mesmo motivo de `RequireSession`:
 * sem ele, o primeiro quadro decidiria com a resposta ainda vazia e jogaria
 * para fora quem tem acesso, a cada abertura de rota protegida.
 *
 * ## ⚠️ O loader só vale para a primeira decisão
 *
 * Quando a sessão muda com a tela já aberta, a chave da consulta muda junto e
 * ela volta a `pending`. Trocar a tela pelo loader nesse momento **desmonta** a
 * rota filha, e tudo o que vivia nela se perde. O caso real é o socorro sem
 * conta (DV-24): a conta anônima nasce no meio do envio, a guarda piscava o
 * loader, e o fluxo SOS remontava no passo 1 com o pedido já gravado — a pessoa
 * nunca via a confirmação. Depois de deixar a tela passar uma vez, a guarda
 * mantém a tela enquanto confere de novo; se a resposta nova for "bloqueada",
 * a tela de bloqueio entra no lugar como sempre.
 */
export function RequireActiveAccount() {
  const { isAccountSuspended, isLoading } = useAccountAccess();
  const [hasPassed, setHasPassed] = useState(false);

  // Ajuste de estado durante o render, o padrão do React para "lembrar do que
  // já aconteceu": sem efeito, sem um quadro a mais com o valor antigo.
  if (!isLoading && !isAccountSuspended && !hasPassed) setHasPassed(true);

  if (isLoading && !hasPassed) return <ArcusLoading className="h-dvh" />;

  if (isAccountSuspended) return <AccountSuspendedScreen />;

  return <Outlet />;
}
